import React from 'react';
import {
  Snackbar as MuiSnackbar,
  SnackbarProps as MuiSnackbarProps,
  Alert,
  AlertProps,
  Slide,
  SlideProps,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { StyledSnackbar } from './Snackbar.style';

type TransitionProps = Omit<SlideProps, 'direction'>;

function SlideTransition(props: TransitionProps) {
  return <Slide {...props} direction="up" />;
}

export interface SnackbarProps extends Omit<MuiSnackbarProps, 'onClose'> {
  /**
   * Whether the snackbar is open
   */
  open: boolean;
  /**
   * Callback fired when the snackbar is closed
   */
  onClose?: (event?: React.SyntheticEvent | Event, reason?: string) => void;
  /**
   * The message to display
   */
  message?: React.ReactNode;
  /**
   * The severity of the snackbar
   */
  severity?: 'success' | 'error' | 'warning' | 'info';
  /**
   * Whether to show a close button
   */
  showCloseButton?: boolean;
  /**
   * Position of the snackbar
   */
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  /**
   * Auto hide duration in milliseconds
   */
  autoHideDuration?: number;
  /**
   * Custom action element
   */
  action?: React.ReactNode;
  /**
   * Whether to use Alert component for styling
   */
  useAlert?: boolean;
  /**
   * Transition component
   */
  TransitionComponent?: React.ComponentType<TransitionProps>;
}

export const Snackbar: React.FC<SnackbarProps> = ({
  open,
  onClose,
  message,
  severity,
  showCloseButton = false,
  position = { vertical: 'bottom', horizontal: 'left' },
  autoHideDuration = 6000,
  action,
  useAlert = false,
  TransitionComponent = SlideTransition,
  ...props
}) => {
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  const closeButton = showCloseButton ? (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <Close fontSize="small" />
    </IconButton>
  ) : null;

  const snackbarAction = action || closeButton;

  if (useAlert && severity) {
    return (
      <StyledSnackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={position}
        TransitionComponent={TransitionComponent}
        {...props}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </StyledSnackbar>
    );
  }

  return (
    <StyledSnackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      message={message}
      action={snackbarAction}
      anchorOrigin={position}
      TransitionComponent={TransitionComponent}
      {...props}
    />
  );
}; 